import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useCRM } from '../context/CRMContext';
import { useLocation } from 'react-router-dom';
import Card from '../components/UI/Card';
import Button from '../components/UI/Button';
import Modal from '../components/UI/Modal';
import SalaryForm from '../components/Forms/SalaryForm';
import ProcessPayrollForm from '../components/Forms/ProcessPayrollForm';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import toast from 'react-hot-toast';
import { format } from 'date-fns';

const { FiDollarSign, FiCheckCircle, FiPrinter, FiEdit, FiSearch, FiHelpCircle, FiX, FiRefreshCw, FiClock, FiList } = FiIcons;

const Payroll = () => {
  const { isAdmin } = useAuth();
  const { users, salaries, payrollRecords, upsertSalary, addPayrollRecord, loadAllData } = useCRM();
  const location = useLocation();
  
  const [activeTab, setActiveTab] = useState('process');
  
  // Salary Structure State
  const [selectedUserForStructure, setSelectedUserForStructure] = useState(null);
  const [isSalaryModalOpen, setIsSalaryModalOpen] = useState(false);
  
  // Process Payroll State
  const [selectedUserForProcess, setSelectedUserForProcess] = useState(null);
  const [isProcessModalOpen, setIsProcessModalOpen] = useState(false);

  // History State
  const [selectedUserForHistory, setSelectedUserForHistory] = useState(null);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const [showHelp, setShowHelp] = useState(true);

  // Handle navigation from Users page
  useEffect(() => {
    if (location.state?.openSalaryFor) {
      setActiveTab('structure');
      setSelectedUserForStructure(location.state.openSalaryFor);
      setIsSalaryModalOpen(true);
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  if (!isAdmin) {
    return (
      <div className="space-y-6">
        <Card className="p-12 text-center">
          <SafeIcon icon={FiDollarSign} className="h-12 w-12 text-red-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Access Denied</h3>
          <p className="text-gray-500">Only administrators can access payroll.</p>
        </Card>
      </div>
    );
  }

  // Helpers
  const getUserName = (u) => u.fullName || u.full_name || '';
  const isUserActive = (u) => u.isActive === true || u.is_active === true;

  // Filter Active Employees
  const activeEmployees = users.filter(u => isUserActive(u));
  const filteredEmployees = activeEmployees.filter(u => 
    getUserName(u).toLowerCase().includes(searchTerm.toLowerCase()) ||
    (u.email && u.email.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const getCurrencySymbol = (curr) => {
    switch(curr) {
      case 'USD': return '$';
      case 'EUR': return '€';
      case 'GBP': return '£';
      case 'AED': return 'AED ';
      case 'INR': return '₹';
      default: return '$';
    }
  };

  const getLastPaidMonth = (userId) => {
    const userRecords = payrollRecords.filter(r => r.userId === userId);
    if (!userRecords || userRecords.length === 0) return 'Never';
    
    // Sort descending by payment month
    const sorted = [...userRecords].sort((a, b) => new Date(b.paymentMonth) - new Date(a.paymentMonth));
    const lastRecord = sorted[0];
    
    return format(new Date(lastRecord.paymentMonth), 'MMMM yyyy');
  };

  // --- Handlers ---

  // 1. Structure
  const handleEditSalary = (user) => {
    setSelectedUserForStructure(user);
    setIsSalaryModalOpen(true);
  };

  const handleSalarySubmit = async (data) => {
    await upsertSalary({
      user_id: selectedUserForStructure.id,
      base_salary: data.baseSalary,
      housing_allowance: data.housingAllowance,
      transport_allowance: data.transportAllowance,
      other_allowances: data.otherAllowances,
      deductions: data.deductions,
      currency: data.currency
    });
    setIsSalaryModalOpen(false);
    setSelectedUserForStructure(null);
  };

  // 2. Process
  const handleOpenProcessModal = (user) => {
    const salary = salaries.find(s => s.userId === user.id);
    if (!salary || parseFloat(salary.baseSalary) === 0) {
      toast.error(`Please configure salary for ${getUserName(user)} first.`);
      setActiveTab('structure');
      handleEditSalary(user);
      return;
    }
    setSelectedUserForProcess(user);
    setIsProcessModalOpen(true);
  };

  const handleProcessSubmit = async (data) => {
    try {
      // Check if duplicate for this month? (Optional warning, but allows re-payment/bonus)
      const exists = payrollRecords.some(r => 
        r.userId === selectedUserForProcess.id && 
        r.paymentMonth.startsWith(data.paymentForMonth)
      );
      
      if (exists) {
        if (!window.confirm(`A payment record for ${data.paymentForMonth} already exists for this user. Continue anyway?`)) {
          return;
        }
      }

      const newRecord = await addPayrollRecord({
        user_id: selectedUserForProcess.id,
        payment_month: `${data.paymentForMonth}-01`, // Format as YYYY-MM-01
        payment_date: new Date(data.paymentDate).toISOString(),
        status: 'paid',
        base_salary: data.baseSalary,
        total_allowances: data.allowances,
        total_deductions: data.deductions,
        net_salary: data.netSalary,
        notes: 'Processed via Payroll Dashboard'
      });
      
      toast.success('Payment recorded successfully!');
      setIsProcessModalOpen(false);
      setSelectedUserForProcess(null);
      
      // Option to print immediately
      if (newRecord?.id) {
        if(window.confirm('Do you want to view the payslip now?')) {
           window.open(`#/print-payslip/${newRecord.id}`, '_blank');
        }
      }
    } catch (error) {
      console.error(error);
      toast.error('Failed to process payment');
    }
  };

  // 3. History
  const handleOpenHistory = (user) => {
    setSelectedUserForHistory(user);
    setIsHistoryModalOpen(true);
  };

  const openPrintView = (recordId) => {
    window.open(`#/print-payslip/${recordId}`, '_blank');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">Payroll Management</h1>
        <div className="flex items-center space-x-2">
          <button 
            onClick={loadAllData} 
            className="p-2 text-gray-500 hover:text-primary-600 rounded-full hover:bg-gray-100"
            title="Refresh Data"
          >
            <SafeIcon icon={FiRefreshCw} className="h-5 w-5" />
          </button>
          <div className="flex bg-gray-100 p-1 rounded-lg">
            <button
              onClick={() => setActiveTab('process')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${activeTab === 'process' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-900'}`}
            >
              Process Payroll
            </button>
            <button
              onClick={() => setActiveTab('structure')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${activeTab === 'structure' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-900'}`}
            >
              Salary Structure
            </button>
          </div>
        </div>
      </div>

      {/* Help Box */}
      {showHelp && (
        <motion.div 
          initial={{ opacity: 0, height: 0 }} 
          animate={{ opacity: 1, height: 'auto' }}
          className="bg-blue-50 border border-blue-200 rounded-lg p-4 relative"
        >
          <button onClick={() => setShowHelp(false)} className="absolute top-2 right-2 text-blue-400 hover:text-blue-600">
            <SafeIcon icon={FiX} className="h-4 w-4" />
          </button>
          <div className="flex items-start space-x-3">
            <SafeIcon icon={FiHelpCircle} className="h-5 w-5 text-blue-600 mt-0.5" />
            <div>
              <h3 className="text-sm font-bold text-blue-900">Payroll Instructions</h3>
              <ol className="list-decimal list-inside text-sm text-blue-800 mt-1 space-y-1">
                <li><strong>Salary Structure:</strong> Define base salary and allowances for each employee first.</li>
                <li><strong>Process Payroll:</strong> Click "Mark Paid" to record a payment for a specific month and date.</li>
                <li><strong>History:</strong> View past payments and generate payslips via the "History" button.</li>
              </ol>
            </div>
          </div>
        </motion.div>
      )}

      {/* Search Bar (Shared) */}
      <Card className="p-4 mb-4">
        <div className="relative">
          <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input 
            type="text" 
            placeholder="Search employees by name or email..." 
            className="w-full pl-9 pr-3 py-2 border rounded-md text-sm focus:ring-primary-500 focus:border-primary-500"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>
      </Card>

      {/* Tab Content: Structure */}
      {activeTab === 'structure' && (
        <Card className="p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Base Salary</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Allowances</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Net Pay (Est)</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredEmployees.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                      No active employees found matching your search.
                    </td>
                  </tr>
                ) : (
                  filteredEmployees.map(user => {
                    const salary = salaries.find(s => s.userId === user.id);
                    const base = salary ? parseFloat(salary.baseSalary) : 0;
                    const allowances = salary ? (parseFloat(salary.housingAllowance) + parseFloat(salary.transportAllowance) + parseFloat(salary.otherAllowances)) : 0;
                    const deductions = salary ? parseFloat(salary.deductions) : 0;
                    const currency = salary?.currency || 'USD';
                    const symbol = getCurrencySymbol(currency);
                    
                    return (
                      <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center text-primary-700 font-bold text-xs mr-3">
                              {getUserName(user).charAt(0)}
                            </div>
                            <div>
                              <div className="text-sm font-medium text-gray-900">{getUserName(user)}</div>
                              <div className="text-xs text-gray-500">{user.role}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {base > 0 ? `${symbol}${base.toLocaleString()}` : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {allowances > 0 ? `${symbol}${allowances.toLocaleString()}` : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {symbol}{(base + allowances - deductions).toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button 
                            onClick={() => handleEditSalary(user)}
                            className="text-primary-600 hover:text-primary-900 flex items-center justify-end w-full"
                          >
                            <SafeIcon icon={FiEdit} className="h-4 w-4 mr-1" /> Configure
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Tab Content: Process */}
      {activeTab === 'process' && (
        <Card className="p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Config Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Paid Month</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredEmployees.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="px-6 py-8 text-center text-gray-500">
                      No active employees found matching your search.
                    </td>
                  </tr>
                ) : (
                  filteredEmployees.map(user => {
                    const hasConfig = salaries.some(s => s.userId === user.id);
                    const lastPaid = getLastPaidMonth(user.id);

                    return (
                      <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                         <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-700 font-bold text-xs mr-3">
                              {getUserName(user).charAt(0)}
                            </div>
                            <div>
                              <div className="text-sm font-medium text-gray-900">{getUserName(user)}</div>
                              <div className="text-xs text-gray-500">{user.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {hasConfig ? (
                            <span className="text-green-600 flex items-center">
                              <SafeIcon icon={FiCheckCircle} className="h-4 w-4 mr-1"/> Ready
                            </span>
                          ) : (
                            <span className="text-red-500 flex items-center text-xs bg-red-50 px-2 py-1 rounded-full w-fit">
                              Missing Config
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          <span className="flex items-center">
                            <SafeIcon icon={FiClock} className="h-4 w-4 mr-2 text-gray-400" />
                            {lastPaid}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex justify-end space-x-2">
                            <button
                              onClick={() => handleOpenHistory(user)}
                              className="text-gray-500 hover:text-gray-900 flex items-center px-3 py-1 rounded-md hover:bg-gray-100 border border-transparent"
                            >
                              <SafeIcon icon={FiList} className="h-4 w-4 mr-1" /> History
                            </button>
                            <Button 
                              size="sm" 
                              onClick={() => handleOpenProcessModal(user)}
                              disabled={!hasConfig}
                            >
                              Mark Paid
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* --- Modals --- */}

      {/* 1. Salary Structure Modal */}
      <Modal 
        isOpen={isSalaryModalOpen} 
        onClose={() => setIsSalaryModalOpen(false)} 
        title="Configure Salary Structure"
        size="lg"
      >
        {selectedUserForStructure && (
          <SalaryForm 
            user={selectedUserForStructure} 
            salary={salaries.find(s => s.userId === selectedUserForStructure.id)} 
            onSubmit={handleSalarySubmit}
            onCancel={() => setIsSalaryModalOpen(false)}
          />
        )}
      </Modal>

      {/* 2. Process Payroll Modal */}
      <Modal
        isOpen={isProcessModalOpen}
        onClose={() => setIsProcessModalOpen(false)}
        title="Process Salary Payment"
        size="lg"
      >
        {selectedUserForProcess && (
          <ProcessPayrollForm
            user={selectedUserForProcess}
            salary={salaries.find(s => s.userId === selectedUserForProcess.id)}
            paidMonths={payrollRecords
              .filter(r => r.userId === selectedUserForProcess.id)
              .map(r => r.paymentMonth.substring(0, 7)) // Extract YYYY-MM
            }
            onSubmit={handleProcessSubmit}
            onCancel={() => setIsProcessModalOpen(false)}
          />
        )}
      </Modal>

      {/* 3. History Modal */}
      <Modal
        isOpen={isHistoryModalOpen}
        onClose={() => setIsHistoryModalOpen(false)}
        title={`Payment History: ${selectedUserForHistory ? getUserName(selectedUserForHistory) : ''}`}
        size="xl"
      >
        {selectedUserForHistory && (
          <div className="space-y-4">
            <div className="overflow-x-auto max-h-96">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">For Month</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Paid On</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Net Amount</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Payslip</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {payrollRecords.filter(r => r.userId === selectedUserForHistory.id).length === 0 ? (
                    <tr>
                      <td colSpan="4" className="px-4 py-6 text-center text-gray-500 text-sm">
                        No payment records found for this employee.
                      </td>
                    </tr>
                  ) : (
                    payrollRecords
                      .filter(r => r.userId === selectedUserForHistory.id)
                      .sort((a, b) => new Date(b.paymentMonth) - new Date(a.paymentMonth))
                      .map(record => {
                        const salaryConfig = salaries.find(s => s.userId === selectedUserForHistory.id);
                        const symbol = getCurrencySymbol(salaryConfig?.currency || 'USD');
                        
                        return (
                          <tr key={record.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm text-gray-900 font-medium">
                              {format(new Date(record.paymentMonth), 'MMMM yyyy')}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">
                              {record.paymentDate ? format(new Date(record.paymentDate), 'MMM dd, yyyy') : '-'}
                            </td>
                            <td className="px-4 py-3 text-sm text-green-600 font-medium">
                              {symbol}{parseFloat(record.netSalary).toLocaleString()}
                            </td>
                            <td className="px-4 py-3 text-right">
                              <button
                                onClick={() => openPrintView(record.id)}
                                className="text-primary-600 hover:text-primary-800 text-sm inline-flex items-center"
                              >
                                <SafeIcon icon={FiPrinter} className="h-4 w-4 mr-1" /> View Slip
                              </button>
                            </td>
                          </tr>
                        );
                      })
                  )}
                </tbody>
              </table>
            </div>
            <div className="flex justify-end pt-4 border-t border-gray-100">
              <Button variant="outline" onClick={() => setIsHistoryModalOpen(false)}>
                Close
              </Button>
            </div>
          </div>
        )}
      </Modal>

    </div>
  );
};

export default Payroll;